import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { useAuth } from '../../AuthContext';
import { useApi } from '../../useApi';
import { useNetwork } from '../../NetworkContext';
import { useOfflineSession } from '../../hooks/useOfflineSession';
import { useCachedScoring } from '../../hooks/useCachedScoring';
import { useSyncStatus } from '../../hooks/useSyncStatus';
import {
  clearOfflineSession, putScoringGroups, putCourse, putPlayersForGroup,
} from '../../offlineDb';
import { ChevronRight, X } from 'lucide-react';
import BottomNav from '../../BottomNav';
import { enterScoresRouteFor } from '../../utils/enterScoresRoute';
import MainMenu from '../../MainMenu';
import {
  rowScores, toLatestRound, rankByRound, feedToPar, recentForm,
  handicapTrend, activityLine, formFootnote, FORM_ROUNDS, FORM_WINDOW,
} from './homeScores';
import WelcomeCard from './WelcomeCard';
import ClaimPlayerSheet from './ClaimPlayerSheet';
import SocietyChip from '../SocietySwitcher';
import HandicapKeypadModal from '../HandicapKeypadModal';
import linksduskSrc from '../Linksdusk.png';
import courseAerialSrc from './course-aerial.jpg';
// The mark with its background keyed out. `public/1_19_logo.png` is the same
// artwork composited on a dark navy tile — fine on a dark page, but over the
// hero photograph it reads as a box dropped on the sky.
import markSrc from './1_19_mark.png';
import { useWeather } from '../../hooks/useWeather';
import {
  REVEAL_CAP_MS, hasAnswered, homeReadiness, readSnapshot, writeSnapshot,
} from './homeReveal';

/* ─── helpers ─────────────────────────────────────────────── */
function getGreeting() {
  const h = new Date().getHours();
  if (h < 5)  return 'Good night';
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}
function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}
function shortName(name) {
  if (!name) return 'Player';
  const p = name.trim().split(' ');
  return p.length === 1 ? p[0] : `${p[0]} ${p[p.length - 1][0]}.`;
}
function fmtDate(s) {
  if (!s) return '';
  try {
    return new Date(s).toLocaleDateString('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short',
    });
  } catch { return ''; }
}

// Rounds carry a DATE, not a timestamp — there is no `created_at` on Round — so
// activity is labelled by day and never claims a time it doesn't have.
function relativeDay(dateStr) {
  if (!dateStr) return '';
  const today = new Date();
  const yday = new Date();
  yday.setDate(yday.getDate() - 1);
  const iso = (d) => d.toISOString().slice(0, 10);
  const d = String(dateStr).slice(0, 10);
  if (d === iso(today)) return 'Today';
  if (d === iso(yday)) return 'Yesterday';
  return fmtDate(dateStr);
}

// The hole a player is on reads as an ordinal — "12th", not "thru 12" — in a
// cell this narrow.
function ordinalHole(n) {
  if (!n) return '';
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

// How long a locally-cached snapshot may still be shown as the first paint.
// The snapshot is what makes Home appear instantly, but it is also what made
// it appear WRONG for a moment — so it is only trusted while it plausibly still
// describes the player's day. Past that, Home waits for the server rather than
// showing a round that may have been superseded.
const CACHED_ROUNDS_TTL = 12 * 60 * 60 * 1000;   // 12 hours

// Shorter than the rounds TTL on purpose: this snapshot says who is out on the
// course RIGHT NOW. A stale round list is merely old; a stale "on course" list
// is wrong. Past this the cards wait for the server instead.
const CACHED_FEED_TTL = 15 * 60 * 1000;   // 15 minutes

// The featured tournament's live board and matches carry the same clock as the
// feed, so the same limit. Without a snapshot of these, a warm open in the
// middle of a round painted recent form and then swapped in the board — the
// most-opened moment of the app was the one that always re-rendered.
const CACHED_LIVE_TTL = CACHED_FEED_TTL;
// A tournament's format does not change while it is being played.
const CACHED_FORMAT_TTL = 24 * 60 * 60 * 1000;

function readCachedFeed(cacheKey) {
  if (!cacheKey) return undefined;
  try {
    const c = JSON.parse(localStorage.getItem(cacheKey) || 'null');
    if (!c || !c.at || Date.now() - c.at > CACHED_FEED_TTL) return undefined;
    return { at: c.at, feed: {
      on_course: Array.isArray(c.on_course) ? c.on_course : [],
      // The list is the shape the card renders; a snapshot written by an
      // older build has only the single `recent_activity`, so fall back to it
      // rather than painting an empty card over a good cache.
      recent_activity_items: Array.isArray(c.recent_activity_items)
        ? c.recent_activity_items
        : (c.recent_activity ? [c.recent_activity] : []),
    } };
  } catch { return undefined; }
}

function readCachedRounds(cacheKey) {
  if (!cacheKey) return undefined;
  try {
    const c = JSON.parse(localStorage.getItem(cacheKey) || 'null');
    if (!c) return undefined;
    // Current shape: { at, rounds }. Older ones: a bare array, or a single
    // round object — both from before the cache was stamped, so they have no
    // age to check and are read once (as age 0, so revalidated at once) and
    // then rewritten by the next fetch.
    if (Array.isArray(c)) return { at: 0, rounds: c };
    if (Array.isArray(c.rounds)) {
      if (!c.at || Date.now() - c.at > CACHED_ROUNDS_TTL) return undefined;
      return { at: c.at, rounds: c.rounds };
    }
    return { at: 0, rounds: [c] };
  } catch { return undefined; }
}

/* ─── Motion presets ──────────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } },
};
const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.06 } },
};
// For a card that arrives AFTER the body has risen (someone else teeing off
// while you are looking): it opens into its place rather than fading up over
// the cards it is pushing down.
const openIn = {
  hidden: fadeUp.hidden,
  show: fadeUp.show,
  closed: { opacity: 0, height: 0, transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] } },
  open: { opacity: 1, height: 'auto', transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] } },
};
// One state of the middle slot giving way to another: the new one fades in
// INSIDE the card, never the card itself leaving and coming back. Only after
// the entrance — before it, the card is still rising as a whole and the swap
// is instant (a fade there only delayed what it was rising to show).
//
// A keyed remount, deliberately NOT AnimatePresence. `mode="wait"` was tried:
// when the slot changed twice in quick succession (placeholder → form → board,
// which a cold open does) it never finished the first exit and left Recent
// Form on screen under a gate that said "board". Measured, not guessed.
const swapIn = { opacity: 0 };
const swapShow = { opacity: 1, transition: { duration: 0.22 } };
// How long the body's own entrance takes (stagger over five cards plus one
// fadeUp); anything mounting after this is a late arrival.
const ENTRANCE_MS = 750;

/* ─── Live leaderboard row ────────────────────────────────── */
// Plain rows, not motion ones: the slot rises as one card. Rows that each ran
// their own entrance faded in one by one whenever a poll changed the top three.
function LbRow({ entry, idx, isStrokePlay, isMe }) {
  const pos = entry.position ?? idx + 1;
  const name = entry.player_name || entry.display_name || 'Player';
  const { toPar, toParCls } = rowScores(entry, isStrokePlay);
  const thru = entry.thru;
  const done = thru >= 18 || !thru;

  return (
    <div className={`ph-lb-row${isMe ? ' ph-lb-me' : ''}`}>
      <span className="ph-lb-pos">{pos}</span>
      <span className="ph-lb-avatar">{getInitials(name)}</span>
      <span className="ph-lb-name">
        {shortName(name)}{isMe ? ' (You)' : ''}
      </span>
      <span className={`ph-lb-topar ${toParCls}`}>{toPar}</span>
      <span className="ph-lb-thru">
        <span className="ph-lb-thru-num">{done ? 'F' : thru}</span>
        <span className="ph-lb-thru-lbl">{done ? 'Today' : 'Thru'}</span>
      </span>
    </div>
  );
}

// Which tournament Home decided to feature, and why. Home picks between the
// active round, the player's last finalised round and a society event today, and
// when it picks differently from what you expect the board type looks wrong for
// no visible reason. `?homedebug=1` shows the resolution; absent otherwise.
const HOME_DEBUG = typeof window !== 'undefined' && /[?&]homedebug=1/.test(window.location.search);

function HomeDebug({ lines }) {
  if (!HOME_DEBUG) return null;
  return (
    <div style={{
      position: 'fixed', left: 4, right: 4, bottom: 96, zIndex: 99999,
      background: 'rgba(0,0,0,0.85)', color: '#0f0', font: '11px/1.35 monospace',
      padding: '6px 8px', borderRadius: 6, whiteSpace: 'pre-wrap', pointerEvents: 'none',
    }}>
      {lines.join('\n')}
    </div>
  );
}

/* ─── Matchplay row ───────────────────────────────────────── */
// Mirrors the matchplay board: each side's players either side of the running
// match score, tinted to whoever is up.
function MpRow({ match }) {
  const lastResult = match.hole_results?.[match.hole_results.length - 1];
  const isAllSquare = match.score === 'A/S' || !lastResult;
  const leader = isAllSquare ? null : lastResult.leader_color;
  const side = (team) => (team || []).map(p => shortName(p.name)).join(' / ') || '—';

  return (
    <div className="ph-mp-row">
      <span className={`ph-mp-team${leader === 'red' ? ' leading' : ''}`}>{side(match.red_team)}</span>
      <span className={`ph-mp-score ph-mp-${leader || 'as'}`}>{match.score}</span>
      <span className={`ph-mp-team ph-mp-team-right${leader === 'blue' ? ' leading' : ''}`}>
        {side(match.blue_team)}
      </span>
    </div>
  );
}

/* ─── A card that may arrive late ─────────────────────────── */
// Present when the body rises, it takes its place in the stagger like every
// other card. Arriving AFTER that (a poll turned up someone on the course), it
// opens into its place, so the cards below slide rather than jump. Decided
// once, at mount: `late` changing later must not re-run an entrance.
function LateCard({ late, className, children }) {
  const [arrivedLate] = useState(late);
  return (
    // The height is animated on a bare wrapper: the card's own padding and
    // border would otherwise stay behind as a 24px sliver at height 0.
    <motion.div
      className="ph-late"
      variants={openIn}
      {...(arrivedLate ? { initial: 'closed', animate: 'open' } : {})}
      exit="closed"
    >
      <div className={className}>{children}</div>
    </motion.div>
  );
}

/* ─── Main component ──────────────────────────────────────── */
export default function LoggedInHome() {
  const navigate = useNavigate();
  const { userProfile, currentSociety, playerStatus, refreshPlayerStatus,
          pendingArchiveNotices, dismissArchiveNotice, currentUser, fetchUserProfile,
          isViewingAsAdmin } = useAuth();
  const { authenticatedFetch } = useApi();
  const { isOnline } = useNetwork();
  const { session, hasSession } = useOfflineSession();
  const { tournamentId: cachedTid, startedGroups, hasStartedGroups } = useCachedScoring();
  const { pendingCount } = useSyncStatus();
  const [showHcp, setShowHcp] = useState(false);
  const [savingHcp, setSavingHcp] = useState(false);
  const weather = useWeather();
  const queryClient = useQueryClient();

  useEffect(() => { refreshPlayerStatus(); }, []); // eslint-disable-line

  // Warm the lazy-loaded scoring route chunks while we're online so they're in
  // the service-worker cache before signal drops. Without this, navigating to
  // the (code-split) group picker or live-scoring page offline rejects the
  // dynamic import with a "failed to load" chunk error. Same module specifiers
  // as App.js, so webpack serves the identical chunks.
  useEffect(() => {
    if (!isOnline) return;
    import('../../ScorecardInput').catch(() => {});
    import('../../LiveScoring').catch(() => {});
  }, [isOnline]);

  // EVERY query on this page waits for `currentUser`, and that is the whole
  // reason the cards used to be blank until you navigated away and back.
  //
  // Home is the one un-gated route (`/` in App.js has no <ProtectedRoute>), so
  // it mounts the instant `userProfile` is read out of localStorage — which is
  // synchronous — while `currentUser` only arrives when Firebase finishes
  // restoring the session. That is NOT a local read: `initializeCurrentUser`
  // always re-validates the account against identitytoolkit, and refreshes the
  // ID token against securetoken whenever the stored one is over an hour old.
  // One or two network round-trips, so seconds on a cold phone radio.
  //
  // `currentSociety` comes out of the same synchronous localStorage read, so
  // `playerId`/`societyId` are already set on that first render and every query
  // here was `enabled` — firing while `authenticatedFetch` could only throw
  // "User not authenticated". React Query's single retry re-runs the SAME
  // captured queryFn (it only re-reads `options.queryFn` in `Query.fetch`, and
  // an in-flight fetch returns its existing promise rather than taking the new
  // options), so the retry failed identically and the query settled into an
  // error with no data. Nothing re-triggered it: `currentUser` was in no key,
  // no `enabled` flag and no dependency. Only the two polled queries recovered,
  // after their 60s interval; the rest stayed blank until a remount.
  //
  // Gating on `currentUser` rather than blocking the page keeps the instant
  // paint from cache — the snapshot renders, the network fills in behind it.

  // Refresh home data when scores sync, offline session changes, or network returns.
  // Without this, finishing a round can leave the home page showing pre-round data
  // for up to 3 minutes (React Query staleTime) — independent of the service worker.
  useEffect(() => {
    const invalidateHome = () => {
      queryClient.invalidateQueries(['recentRounds']);
      queryClient.invalidateQueries(['homeLeaderboard']);
      queryClient.invalidateQueries(['lastTournamentLb']);
      queryClient.invalidateQueries(['liveLb']);
      queryClient.invalidateQueries(['homeFeed']);
      refreshPlayerStatus();
    };
    window.addEventListener('syncQueueChanged', invalidateHome);
    window.addEventListener('offlineSessionChanged', invalidateHome);
    window.addEventListener('online', invalidateHome);
    return () => {
      window.removeEventListener('syncQueueChanged', invalidateHome);
      window.removeEventListener('offlineSessionChanged', invalidateHome);
      window.removeEventListener('online', invalidateHome);
    };
  }, [queryClient, refreshPlayerStatus]);

  const playerId  = currentSociety?.player_id;
  const societyId = currentSociety?.id;
  const cacheKey  = societyId && playerId ? `cached_last_round_${societyId}_${playerId}` : null;
  const roundsSnap = useMemo(() => readCachedRounds(cacheKey), [cacheKey]);

  const roundsQ = useQuery(
    ['recentRounds', societyId, playerId],
    async () => {
      if (!playerId) return [];
      // The cards this page shows, in the summary shape: newest first, no hole
      // scores and no course blob. Unfiltered, this
      // endpoint returns every round the player has ever played with all 18
      // holes and the whole course attached to each, which is the single most
      // expensive thing Home did.
      //
      // MORE THAN THE FIVE IT DRAWS (`FORM_WINDOW`), because `formToPar` drops
      // a round it cannot read — a match has no total worth reading — and
      // asking for exactly five meant a player coming off a matchplay weekend
      // got three tiles under a heading that promises five. `recentForm` takes
      // the newest five READABLE rounds out of these and reports what it passed
      // over; `handicapTrend` is then measured over that same five, so the
      // arrow beside HCP and the tiles below it still describe one run of golf.
      //
      // NOT `finalized=1`, which this was and which no other screen asks for.
      // Finalising is the ORGANISER's act — it applies the handicap revision —
      // and gating a player's own form on it meant a society that had not got
      // round to finalising left its members with three tiles where they had
      // played eight rounds. `isFormCard` asks the question that was actually
      // meant: was the card played to the end. The `holes_scored` it reads
      // comes down with this summary shape.
      //
      // The newest-first ordering (date, then id — several rounds share a date
      // on a multi-round day) is the server's now, so it can be served from the
      // index; getting it wrong there meant the OLDEST round won and Home
      // featured the wrong tournament.
      const res = await authenticatedFetch(
        `/rounds?player_id=${playerId}&limit=${FORM_WINDOW}&summary=1`);
      // THROW, never `return []`. A resolved empty array is a successful result
      // as far as React Query is concerned, so it replaces the cached rounds
      // this query was seeded with — one 401 mid token-refresh and the page
      // goes blank. Throwing keeps the last good data on screen.
      if (!res.ok) throw new Error(`/rounds ${res.status}`);
      const rounds = await res.json();
      if (!Array.isArray(rounds)) throw new Error('/rounds: unexpected shape');
      if (rounds.length && cacheKey) {
        try {
          localStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), rounds }));
        } catch {}
      }
      return rounds;
    },
    {
      enabled: !!playerId && !!currentUser,
      // NOT `refetchOnMount: 'always'`. Every one of this page's queries used
      // to carry it, so arriving on Home — even with a cache seconds old —
      // painted the cached answer and then refetched and repainted, one slice
      // at a time as each landed. That is the stale-then-jump, and the
      // staggered redraw, in one option. `staleTime` already says how old an
      // answer may be, and the live ones have a `refetchInterval` of their own.
      staleTime: 15 * 1000,
      initialData: roundsSnap?.rounds,
      // As old as when it was WRITTEN. Without this React Query dates seeded
      // data to the moment it was read, calls it fresh for `staleTime`, and
      // never asks the server on this visit at all.
      initialDataUpdatedAt: roundsSnap?.at,
    }
  );
  const recentRoundsRaw = roundsQ.data;
  const recentRounds = Array.isArray(recentRoundsRaw) ? recentRoundsRaw : [];

  // The two society-wide cards, in one request. Society comes from the request
  // header, as it does for /play-feed and /me/status.
  // Keyed by society AND PLAYER, like the rounds snapshot beside it. The feed
  // looks society-wide but is not: `on_course` has the viewer's own round taken
  // out of it, and `recent_activity` carries `is_you`, which the card renders
  // as "You shot 78". Under a society-only key, the next person to sign in on
  // this device read the last one's answer as their own.
  const feedCacheKey = societyId && playerId
    ? `cached_home_feed_${societyId}_${playerId}` : null;
  const feedSnap = useMemo(() => readCachedFeed(feedCacheKey), [feedCacheKey]);
  const feedQ = useQuery(
    ['homeFeed', societyId],
    async () => {
      const res = await authenticatedFetch('/home-feed');
      // THROW, never return an empty shape. A resolved `{on_course: [], ...}`
      // is a successful answer as far as React Query is concerned, so one 401
      // mid token-refresh would replace the cached feed and the cards would
      // claim the society is quiet. Throwing keeps the last good data.
      if (!res.ok) throw new Error(`/home-feed ${res.status}`);
      const body = await res.json();
      const feed = {
        on_course: Array.isArray(body?.on_course) ? body.on_course : [],
        // Two cards, two players. `recent_activity` is the same list's first
        // item, still served for the shipped native builds — read the list.
        recent_activity_items: Array.isArray(body?.recent_activity_items)
          ? body.recent_activity_items
          : (body?.recent_activity ? [body.recent_activity] : []),
      };
      if (feedCacheKey) {
        try {
          localStorage.setItem(feedCacheKey, JSON.stringify({ at: Date.now(), ...feed }));
        } catch {}
      }
      return feed;
    },
    {
      enabled: !!societyId && !!currentUser,
      // NOT `refetchOnMount: 'always'`. Every one of this page's queries used
      // to carry it, so arriving on Home — even with a cache seconds old —
      // painted the cached answer and then refetched and repainted, one slice
      // at a time as each landed. That is the stale-then-jump, and the
      // staggered redraw, in one option. `staleTime` already says how old an
      // answer may be, and the live ones have a `refetchInterval` of their own.
      staleTime: 30 * 1000,
      refetchInterval: 60000,
      initialData: feedSnap?.feed,
      initialDataUpdatedAt: feedSnap?.at,
    }
  );
  const homeFeed = feedQ.data;
  const elsewhere = homeFeed?.on_course ?? [];
  const activityItems = homeFeed?.recent_activity_items ?? [];
  // Whether the society cards have an ANSWER yet, as opposed to no news. The
  // two look identical in the data — both give an empty list — and
  // rendering "Nothing yet today" for the first is the difference between a
  // quiet society and a broken page.
  const feedKnown = homeFeed !== undefined;

  const lbTid = playerStatus.status === 'active_round' ? playerStatus.tournament_id : null;
  const summaryQ = useQuery(
    ['homeLeaderboard', lbTid],
    async () => {
      const res = await authenticatedFetch(`/tournaments/${lbTid}/leaderboard_summary`);
      if (!res.ok) throw new Error(`/leaderboard_summary ${res.status}`);
      const data = await res.json();
      if (!Array.isArray(data)) throw new Error('/leaderboard_summary: unexpected shape');
      return data;
    },
    // See the note on the rounds query: no `refetchOnMount: 'always'`.
    { enabled: !!lbTid && !!currentUser, staleTime: 15 * 1000 }
  );
  const leaderboardRaw = summaryQ.data;
  const leaderboard = Array.isArray(leaderboardRaw) ? leaderboardRaw : [];

  // Warm the live-leaderboard cache while the user is still on Home, so tapping
  // through to the leaderboard paints instantly instead of showing a spinner
  // during a cold /live_leaderboard fetch on slow networks. The query key must
  // match LiveLeaderboardDisplay's exactly: [tournamentId, leaderboardType, round].
  useEffect(() => {
    if (!lbTid || !currentUser) return;
    queryClient.prefetchQuery(
      ['liveLeaderboard', lbTid, 'individual', ''],
      async () => {
        // slim=1: see LiveLeaderboardDisplay — same board, same cache key,
        // so it must ask for the same shape.
        const res = await authenticatedFetch(`/tournaments/${lbTid}/live_leaderboard?slim=1`);
        if (!res.ok) throw new Error('live leaderboard prefetch failed');
        const data = await res.json();
        return { ...data, leaderboard: data.leaderboard || [] };
      },
      // Keep it short-lived so the page still revalidates (and resolves the
      // active round via its onSuccess) right after the instant cached paint.
      { staleTime: 5 * 1000 }
    );
  }, [lbTid, currentUser, queryClient, authenticatedFetch]);

  // Find a society-wide live tournament when the user isn't in one.
  // Anything dated today (or yesterday for late finishes) is a candidate;
  // we'll filter to players with actual scoring activity below.
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = (() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  })();
  const liveSocietyTid = (() => {
    if (lbTid) return null;
    const all = [
      ...(playerStatus.upcoming_tournaments || []),
      ...(playerStatus.available_rollups || []),
    ];
    const t = all.find(x => {
      const d = String(x.tournament_date || '').slice(0, 10);
      return d === today || d === yesterday;
    });
    return t?.tournament_id || null;
  })();

  // ...and the round you are NOT in. `liveSocietyTid` above is built from
  // `upcoming_tournaments` + `available_rollups`, which is only what you are
  // entered in or could still join — so a society competition you are not part
  // of never reached the board, and Home fell back to recent form while golf
  // was being played. `/home-feed` scans every society tournament today, so it
  // knows about that round; take whichever one has the most players out.
  const elsewhereTid = (() => {
    if (lbTid || liveSocietyTid || !elsewhere.length) return null;
    const byTournament = new Map();
    for (const p of elsewhere) {
      byTournament.set(p.tournament_id, (byTournament.get(p.tournament_id) || 0) + 1);
    }
    return [...byTournament.entries()].sort((a, b) => b[1] - a[1])[0][0];
  })();

  // Live (in-progress) figures for the board, not the finalized summary.
  // /live_leaderboard returns: thru, stableford_points, group_number
  const ocTid = lbTid || liveSocietyTid || elsewhereTid;
  // Keyed by society, like every snapshot on this page, and by tournament.
  const liveSnapKey = societyId && ocTid ? `cached_home_live_${societyId}_${ocTid}` : null;
  const liveSnap = useMemo(() => readSnapshot(liveSnapKey, CACHED_LIVE_TTL), [liveSnapKey]);
  const liveQ = useQuery(
    ['liveLb', ocTid],
    async () => {
      const res = await authenticatedFetch(`/tournaments/${ocTid}/live_leaderboard`);
      if (!res.ok) throw new Error(`/live_leaderboard ${res.status}`);
      const body = await res.json();
      // /live_leaderboard returns { leaderboard: [...], format, ... }
      const arr = Array.isArray(body) ? body : body?.leaderboard;
      const rows = Array.isArray(arr) ? arr : [];
      writeSnapshot(liveSnapKey, rows);
      return rows;
    },
    {
      enabled: !!ocTid && !!currentUser,
      refetchInterval: 60000,
      // See the note on the rounds query.
      staleTime: 15 * 1000,
      initialData: liveSnap?.value,
      initialDataUpdatedAt: liveSnap?.at,
    }
  );
  const ocLiveRaw = liveQ.data;
  const ocLive = Array.isArray(ocLiveRaw) ? ocLiveRaw : [];
  // Has anyone actually started scoring? Everyone still on the first tee is not
  // a live board — it's three rows of "—".
  const hasLiveScoring = ocLive.some(e => (e.thru || 0) > 0);

  // The format of whatever is being played, which decides whether the board
  // reads to-par as points pace or nett strokes, and whether it is a board at
  // all rather than a set of matches.
  const mpTid = lbTid || liveSocietyTid || elsewhereTid;
  const formatSnapKey = societyId && mpTid ? `cached_home_format_${societyId}_${mpTid}` : null;
  const formatSnap = useMemo(() => readSnapshot(formatSnapKey, CACHED_FORMAT_TTL), [formatSnapKey]);
  const formatQ = useQuery(
    ['homeTournamentFormat', mpTid],
    async () => {
      const res = await authenticatedFetch(`/tournaments/${mpTid}`);
      if (!res.ok) return '';
      const body = await res.json();
      const format = body?.format || '';
      writeSnapshot(formatSnapKey, format);
      return format;
    },
    {
      enabled: !!mpTid && !!currentUser,
      staleTime: 5 * 60 * 1000,
      initialData: formatSnap?.value,
      initialDataUpdatedAt: formatSnap?.at,
    }
  );
  const tournamentFormat = formatQ.data;
  const isMatchplay = /match\s*play/i.test(tournamentFormat || '');
  // Matches the server's own test in leaderboard_summary / live_leaderboard, and
  // decides whether to-par means nett strokes or points pace.
  const isStrokePlay = (tournamentFormat || '') === 'Stroke Play';

  // Matchplay tournaments are decided by the match score, so the middle slot
  // shows the matches rather than to-par and points. No round_number: the
  // endpoint defaults to the latest round, which is the one being played.
  const matchesSnapKey = societyId && mpTid ? `cached_home_matches_${societyId}_${mpTid}` : null;
  const matchesSnap = useMemo(() => readSnapshot(matchesSnapKey, CACHED_LIVE_TTL), [matchesSnapKey]);
  const matchesQ = useQuery(
    ['homeMatchplay', mpTid],
    async () => {
      const res = await authenticatedFetch(`/tournaments/${mpTid}/matchplay-leaderboard`);
      if (!res.ok) throw new Error(`/matchplay-leaderboard ${res.status}`);
      const body = await res.json();
      const matches = Array.isArray(body) ? body : [];
      writeSnapshot(matchesSnapKey, matches);
      return matches;
    },
    {
      enabled: !!mpTid && isMatchplay && !!currentUser,
      refetchInterval: 60000,
      staleTime: 15 * 1000,
      initialData: matchesSnap?.value,
      initialDataUpdatedAt: matchesSnap?.at,
    }
  );
  const mpMatchesRaw = matchesQ.data;
  const mpMatches = Array.isArray(mpMatchesRaw) ? mpMatchesRaw : [];

  // Prime the offline cache for the live/active tournament while we're online,
  // so scoring stays reachable after signal drops on-course — even if the user
  // never opened the group picker or a scorecard first. Caches the scoring
  // groups (already permission-scoped by the backend), each group's course, and
  // the players for started groups. Runs once per tournament per mount.
  // The `currentUser` guard is not belt-and-braces here, it is the difference
  // between having a cached round on the course and not: without it this ran on
  // the first render, threw "User not authenticated" — and had ALREADY claimed
  // the tournament in `precachedTidRef`, so the re-run when auth landed was
  // turned away by its own guard. The ref is now only claimed once the groups
  // are actually in hand.
  const precachedTidRef = useRef(null);
  useEffect(() => {
    if (!isOnline || !ocTid || !currentUser || precachedTidRef.current === ocTid) return;
    let cancelled = false;

    // AFTER THE PAGE, NOT ALONGSIDE IT. This is speculative work — priming
    // IndexedDB so the scoring card opens offline later — and it is one
    // request plus one per course and one per started group. Fired on mount it
    // competed for the connection with the page the reader is waiting for,
    // which on a weak link is how a background convenience becomes a visible
    // delay. It waits for a quiet moment instead; late is fine, because
    // nothing on screen depends on it.
    let start;
    const run = async () => {
      try {
        const res = await authenticatedFetch(`/tournaments/${ocTid}/scoring_groups`);
        if (!res.ok) return;
        const data = await res.json();
        const groups = data?.groups || [];
        if (cancelled || !groups.length) return;
        precachedTidRef.current = ocTid;

        await putScoringGroups(String(ocTid), groups);

        const seenCourses = new Set();
        for (const grp of groups) {
          // Course data (deduped per course+tee).
          const courseKey = `${grp.course_id}-${grp.tee_id || ''}`;
          if (grp.course_id && !seenCourses.has(courseKey)) {
            seenCourses.add(courseKey);
            try {
              const teeParam = grp.tee_id ? `?tee_id=${grp.tee_id}` : '';
              const cRes = await authenticatedFetch(`/courses/${grp.course_id}${teeParam}`);
              if (cRes.ok) putCourse(await cRes.json()).catch(() => {});
            } catch { /* cached on demand later */ }
          }
          // Players for started groups (needed to render the scorecard offline).
          if (grp.is_started && grp.round_number != null && grp.group_number != null) {
            try {
              const pRes = await authenticatedFetch(
                `/tournaments/${ocTid}/rounds/${grp.round_number}/groups/${grp.group_number}/players`
              );
              if (pRes.ok) {
                const players = (await pRes.json()).map(p => ({
                  ...p, playing_handicap: p.playing_handicap ?? 0,
                }));
                await putPlayersForGroup(String(ocTid), grp.round_number, grp.group_number, players);
              }
            } catch { /* cached on demand later */ }
          }
        }
        if (!cancelled) window.dispatchEvent(new Event('scoringGroupsCached'));
      } catch { /* offline-prime is best-effort */ }
    };

    if (typeof window.requestIdleCallback === 'function') {
      start = window.requestIdleCallback(run, { timeout: 5000 });
    } else {
      start = setTimeout(run, 1500);
    }

    return () => {
      cancelled = true;
      if (typeof window.cancelIdleCallback === 'function' && start != null) {
        window.cancelIdleCallback(start);
      } else {
        clearTimeout(start);
      }
    };
  }, [isOnline, ocTid, currentUser, authenticatedFetch]);

  // Board data:
  // - In an active round: prefer live data (live_leaderboard) so the board shows
  //   mid-round positions instead of going blank until rounds finalize.
  //   Normalise the live shape (stableford_points -> total_stableford) and
  //   carry score_to_par + thru through so to-par renders correctly.
  // - Otherwise: use the last finished tournament's summary.
  const liveTileEntries = ocLive.map(e => ({
    player_id: e.player_id,
    player_name: e.player_name,
    position: e.position,
    total_stableford: e.stableford_points ?? 0,
    total_nett: e.net_score,
    total_par: null,
    score_to_par: e.score_to_par,
    thru: e.thru,
    rounds_played: 0,
  }));
  // Only count live entries with actual scoring activity — otherwise the board
  // shows three "—" rows while everyone is still on the tee.
  const liveScoringEntries = liveTileEntries.filter(e =>
    (e.thru || 0) > 0 || (e.total_stableford || 0) > 0
  );
  // Summary fallbacks are narrowed to their latest round and re-ranked, so the
  // figures on these cards are the same ones the leaderboard shows.
  const roundScopedLb = rankByRound(toLatestRound(leaderboard), isStrokePlay);

  // The middle slot shows a BOARD only while golf is actually being played —
  // its other state is recent form. A finished tournament's standings are what
  // the Leaderboard tab is for, and showing them here left a player who wasn't
  // out today looking at last month's result instead of their own form.
  const liveTid = lbTid || (hasLiveScoring ? (liveSocietyTid || elsewhereTid) : null);
  // A matchplay tournament never falls back to points and to-par — if the
  // matches aren't loaded yet the slot waits rather than changing game.
  const boardIsMatchplay = !!liveTid && isMatchplay && mpMatches.length > 0;
  const boardEntries = (!liveTid || boardIsMatchplay)
    ? []
    : (liveScoringEntries.length ? liveScoringEntries : roundScopedLb);
  // The design's board is the top three of the round you are in, with your own
  // row picked out — not a window centred on you.
  const boardTop3 = boardEntries.slice(0, 3);

  // Once the board above is showing a tournament, that tournament's players are
  // not "elsewhere" any more — they are the card the reader just looked at.
  const elsewhereShown = liveTid
    ? elsewhere.filter(p => String(p.tournament_id) !== String(liveTid))
    : elsewhere;

  // ── One entrance, once the page knows its shape ──────────
  // See homeReveal.js. The hero paints at once (it needs nothing but the
  // profile); the body under it rises as ONE stagger when every answer that
  // decides which cards it holds is in — instantly on a warm open, where the
  // snapshots answer everything — or at REVEAL_CAP_MS, whichever is first.
  // Offline, nothing more is coming, so it does not wait.
  const readiness = homeReadiness({
    status: playerStatus.status !== 'loading',
    rounds: !playerId || hasAnswered(roundsQ),
    feed: !societyId || hasAnswered(feedQ),
    boardTid: ocTid,
    live: hasAnswered(liveQ),
    format: hasAnswered(formatQ),
    matchplay: isMatchplay,
    matches: hasAnswered(matchesQ),
    // The summary board is only ever the FALLBACK, for a round you are in
    // whose live board has no scores on it yet — so it holds nothing back
    // while the live board (or its snapshot) already has rows to show.
    summaryNeeded: !!lbTid && hasAnswered(liveQ) && liveScoringEntries.length === 0,
    summary: hasAnswered(summaryQ),
  });
  const [capFor, setCapFor] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => setCapFor(societyId ?? 'none'), REVEAL_CAP_MS);
    return () => clearTimeout(t);
  }, [societyId]);
  // Latched per society: once the body has risen it stays up, and later
  // answers change it IN PLACE. A refetch is never a second entrance.
  const revealRef = useRef({ society: undefined, on: false });
  if (revealRef.current.society !== societyId) revealRef.current = { society: societyId, on: false };
  if (readiness.ready || capFor === (societyId ?? 'none') || !isOnline) revealRef.current.on = true;
  const revealed = revealRef.current.on;
  // Anything mounting after the entrance has played is a late arrival and
  // opens into place instead (see `openIn`).
  const [enteredFor, setEnteredFor] = useState(null);
  useEffect(() => {
    if (!revealed) return undefined;
    const t = setTimeout(() => setEnteredFor(societyId ?? 'none'), ENTRANCE_MS);
    return () => clearTimeout(t);
  }, [revealed, societyId]);
  const entered = revealed && enteredFor === (societyId ?? 'none');

  // What the middle slot is showing. 'pending' only ever reaches the screen
  // past the cap: a placeholder in the card's shape rather than recent form
  // standing in for a board that is about to replace it.
  const slotKind = readiness.slotPending ? 'pending'
    : boardIsMatchplay ? 'matchplay'
    : boardTop3.length ? 'board'
    : readiness.roundsPending ? 'pending'
    : 'form';
  // Whether the slot holds (or is about to hold) a board, which decides how
  // the slot and the round card share the page's slack.
  const slotIsBoard = slotKind === 'matchplay' || slotKind === 'board'
    || (slotKind === 'pending' && !!ocTid);

  // Clear stale offline session only once we're online AND have a definitive
  // "no active round" answer from the server. Guarding against 'loading'/empty
  // states stops a transient status from deleting an in-progress session.
  useEffect(() => {
    const settledNotActive = ['idle', 'upcoming'].includes(playerStatus.status);
    if (isOnline && hasSession && settledNotActive) {
      clearOfflineSession().then(() => window.dispatchEvent(new Event('offlineSessionChanged')));
    }
  }, [isOnline, hasSession, playerStatus.status]);

  // A system admin viewing a society they have not joined has a society to
  // show — otherwise Home offered them the join/create welcome card while the
  // rest of the app was already in that society.
  const hasSocieties = !!userProfile?.societies?.length || isViewingAsAdmin;
  const firstName    = userProfile?.display_name?.split(' ')[0] || 'Golfer';
  const initials     = getInitials(userProfile?.display_name);

  const socData      = userProfile?.societies?.find(s => String(s.id) === String(currentSociety?.id));
  const playerProf   = socData?.player_profile;
  const handicap     = playerProf?.handicap;

  const isActive = playerStatus.status === 'active_round';
  // Movement of the number printed beside it — decided by handicapTrend in
  // homeScores.js so scoreToPar.test.js can pin it. Just the movement: "this
  // season" was measured off the end of the glass strip, and next to "HCP 9.4"
  // the arrow already says what the number is.
  // The tiles decide the window, and the arrow follows them — see the fetch
  // above. Nothing readable at all (a run of pure matchplay) falls back to the
  // five newest rounds, so the arrow still has something to measure.
  const formInsights = recentForm(recentRounds);
  const hcpTrend = handicapTrend(
    formInsights?.window?.length ? formInsights.window : recentRounds.slice(0, FORM_ROUNDS),
    handicap);
  // When the user has no future-dated rounds, fall back to the active round
  // so the Upcoming card isn't blank mid-tournament.
  const activeAsUpcoming = isActive && playerStatus.tournament_id ? {
    tournament_id: playerStatus.tournament_id,
    tournament_name: playerStatus.tournament_name,
    tournament_date: playerStatus.tournament_date,
    user_signed_up: true,
    is_current: true,
  } : null;
  const nextUp = playerStatus.upcoming_tournaments?.[0]
              || playerStatus.available_rollups?.[0]
              || activeAsUpcoming;

  // ── Recent form over the last (up to) 5 rounds ──────
  // Every figure decided by `recentForm` in homeScores.js, so the tiles and
  // scoreToPar.test.js cannot disagree — and so a Stroke Play round is never
  // again reported in Stableford points.
  const insights = formInsights;

  // Offline group-picker target: prefer the in-progress session's tournament,
  // otherwise the most-recently-cached tournament with started groups.
  const offlineScoringTid = (!isOnline)
    ? (session?.tournamentId ?? (hasStartedGroups ? cachedTid : null))
    : null;

  /* Round card content + routing.
     `action` and `cta` are the EXISTING pairs, unchanged — the redesign
     restyles this card, it does not re-route it. Everything else here is
     presentation the new layout needs. */
  const feat = (() => {
    if (!isOnline && hasSession) return {
      live: true,
      eyebrow: `OFFLINE · ROUND ${session.roundNumber}`,
      title: session.course?.name || 'Round in Progress',
      metaA: `Group ${session.groupNumber}`,
      metaB: pendingCount > 0 ? `${pendingCount} to sync` : null,
      cta: 'Continue Scoring', ctaKind: 'blue',
      action: () => navigate(`/live-scoring/${session.tournamentId}/${session.groupId}`),
    };
    // Offline with no in-progress session, but a cached round is available:
    // route into the (offline-capable) group picker to start entering scores.
    if (!isOnline && offlineScoringTid) return {
      live: true,
      eyebrow: 'OFFLINE · READY',
      title: 'Round Ready to Score',
      metaA: startedGroups.length > 1
        ? `${startedGroups.length} groups available`
        : 'Enter scores for your group',
      metaB: null,
      cta: 'Enter Scores', ctaKind: 'blue',
      action: () => navigate(
        `/tournaments/${offlineScoringTid}/scorecard-input`,
        { state: startedGroups.length > 0 ? { courseSequence: startedGroups[0].round_number } : {} }
      ),
    };
    // Not "nothing on" — the server has not said yet. A placeholder in the
    // card's shape, never an invitation that turns into your round a moment
    // later.
    if (readiness.statusPending) return { pending: true, live: false };
    if (isActive) return {
      live: true,
      eyebrow: `ROUND ${playerStatus.round_number} · LIVE NOW`,
      title: playerStatus.tournament_name || 'Round in Progress',
      // Both come from /me/status; either may be absent (no tee sheet drawn,
      // no course set) and the row simply drops that half.
      metaA: playerStatus.player_count ? `${playerStatus.player_count} players` : null,
      metaB: playerStatus.course_name || fmtDate(playerStatus.tournament_date),
      statValue: playerStatus.tee_time || null,
      statLabel: playerStatus.tee_time ? 'YOUR TEE TIME' : null,
      // To the CARD — the same route as the bar's Scoring tab. This said
      // "Resume Scoring" and opened the leaderboard, where the player then
      // had to find "Enter Score" and a group picker.
      cta: 'Enter scores', ctaKind: 'blue',
      action: () => {
        const { route, state } = enterScoresRouteFor(playerStatus);
        navigate(route, { state });
      },
    };
    if (nextUp) return {
      live: false,
      eyebrow: 'READY TO PLAY',
      title: nextUp.tournament_name || 'Upcoming Round',
      metaA: fmtDate(nextUp.tournament_date),
      metaB: null,
      statValue: weather ? `${weather.temp}°` : null,
      statLabel: weather ? 'RIGHT NOW' : null,
      cta: nextUp.user_signed_up ? 'View Tee Sheet' : 'Join Round',
      ctaKind: nextUp.user_signed_up ? 'ghost' : 'green',
      action: () => navigate(`/tournaments/${nextUp.tournament_id}/teesheet`),
    };
    return {
      live: false,
      eyebrow: 'READY TO PLAY',
      title: 'Start or join a round',
      metaA: null,
      metaB: null,
      statValue: weather ? `${weather.temp}°` : null,
      statLabel: weather ? 'RIGHT NOW' : null,
      cta: 'Play Golf', ctaKind: 'green',
      action: () => navigate('/play'),
    };
  })();

  // The line under the greeting: what's on, and where.
  // The line under your name is the SOCIETY — a chip that switches it when you
  // have more than one. It used to repeat the round card below it ("Round 2 ·
  // course", "Next round Sat 25 Oct") and only named the society when nothing
  // was booked, so most days Home never said which society it was showing.

  const saveHcp = async (val) => {
    setSavingHcp(true);
    try {
      const res = await authenticatedFetch(`/players/${playerProf.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handicap: val }),
      });
      if (res.ok && currentUser) await fetchUserProfile(currentUser);
    } catch (e) { console.error(e); }
    setSavingHcp(false);
    setShowHcp(false);
  };

  const boardTid = liveTid || ocTid;

  /* ────────────────────────────────── render ── */
  return (
    <div className="ph-page">
      {/* Rendered here, NOT inside .ph-hero: the hero sets `isolation: isolate`
          for the photo blend, which would trap the drawer's z-index inside it
          and paint the page content over the open menu. Its toggle is placed by
          .ph-page's own rule, into the gap the hero's brand row leaves. */}
      <MainMenu inline />

      <HomeDebug lines={[
        `status=${playerStatus.status} lbTid=${lbTid ?? '-'} liveTid=${liveTid ?? '-'} liveSocietyTid=${liveSocietyTid ?? '-'}`,
        `featuring mpTid=${mpTid ?? '-'} format="${tournamentFormat ?? '(none)'}" matchplay=${isMatchplay} matches=${mpMatches.length}`,
        `board=${boardIsMatchplay ? 'matchplay' : 'stroke'} rows=${boardTop3.length} elsewhereTid=${elsewhereTid ?? '-'} elsewhere=${elsewhere.length}/${elsewhereShown.length}`,
        `round card: players=${playerStatus.player_count ?? '-'} course=${playerStatus.course_name ?? '-'} tee=${playerStatus.tee_time ?? '-'}`,
        `reveal: ${revealed ? 'up' : 'held'} ready=${readiness.ready} slot=${slotKind} pending=${
          ['status', 'rounds', 'feed', 'slot'].filter(k => readiness[`${k}Pending`]).join(',') || 'none'}`,
      ]} />

      {/* Archive notices sit ABOVE the hero: the hero has a fixed height in this
          layout, and a banner injected into it pushed the greeting out of it. */}
      {pendingArchiveNotices.map(s => (
        <div key={s.id} className="society-archived-banner">
          <span><strong>{s.name}</strong> has been shut down.</span>
          <button className="society-archived-dismiss" onClick={() => dismissArchiveNotice(s.id)}>
            <X size={16} />
          </button>
        </div>
      ))}

      {/* ══ HERO ══════════════════════════════════════════════ */}
      <div className="ph-hero">
        {/* Three stacked layers, in order: the photo (masked so it fades out
            rather than ending on an edge), a colour-unify layer that pulls it
            toward the app palette, and plain legibility scrims. */}
        <div className="ph-photo ph-photo-hero"
             style={{ backgroundImage: `url(${linksduskSrc})` }} aria-hidden="true" />
        <div className="ph-tint ph-tint-hero" aria-hidden="true" />
        <div className="ph-scrim-hero-v" aria-hidden="true" />
        <div className="ph-hero-hairline" aria-hidden="true" />

        {/* Floating chrome. Nothing here is a bar: the menu circle (placed by
            .ph-page, since MainMenu must render outside this stacking context)
            and the avatar sit in opposite corners OF THE PHOTO, with the mark
            centred between them. */}
        <div className="ph-hero-top">
          <img src={markSrc} alt="1-19" className="ph-logo" />
          <button className="ph-glass-circle ph-hero-avatar"
                  onClick={() => navigate('/profile')} aria-label="Profile">
            {initials}
          </button>
        </div>

        <motion.div className="ph-hero-name" initial="hidden" animate="show" variants={stagger}>
          <motion.div className="ph-greeting-eyebrow" variants={fadeUp}>{getGreeting()},</motion.div>
          <motion.h1 className="ph-greeting-name" variants={fadeUp}>{firstName}</motion.h1>
          <motion.div className="ph-hero-context" variants={fadeUp}>
            <SocietyChip variant="hero" />
          </motion.div>
        </motion.div>

        {/* One glass strip, carrying the two things that are true of the day
            rather than of the round: your index and the weather. */}
        <div className="ph-hero-strip">
          {hasSocieties && (
            <button
              className="ph-strip-hcp"
              onClick={playerProf ? () => setShowHcp(true) : undefined}
              aria-label="Handicap index"
            >
              <span className="ph-strip-label">HCP</span>
              <span className="ph-strip-hcp-num">
                {handicap != null ? handicap.toFixed(1) : '—'}
              </span>
              {handicap != null && hcpTrend && (
                <span className={`ph-strip-trend${hcpTrend.down ? '' : ' up'}`}
                      title={hcpTrend.title}>
                  {hcpTrend.arrow}{hcpTrend.label}
                </span>
              )}
              {handicap == null && <span className="ph-strip-trend muted">Set</span>}
            </button>
          )}
          {weather && (
            <div className="ph-strip-wx">
              <span className="ph-wx"><span className="ph-wx-emoji">{weather.emoji}</span>{weather.temp}°</span>
              <span className="ph-wx-sep" />
              <span className="ph-wx"><span className="ph-wx-wind" />{weather.windSpeed} mph {weather.windDir}</span>
              <span className="ph-wx-sep" />
              <span className="ph-wx"><span className="ph-wx-rain" />{weather.rainChance}%</span>
            </div>
          )}
        </div>
      </div>
      {/* ══ end hero ══════════════════════════════════════════ */}

      {hasSocieties ? (
        <motion.div
          // Remounted per society, so a switch is one fresh entrance rather
          // than the old society's cards changing under the new name.
          key={societyId ?? 'none'}
          className={`ph-body${revealed ? '' : ' is-held'}`}
          initial="hidden" animate={revealed ? 'show' : 'hidden'} variants={stagger}
          aria-busy={!revealed}>

          {/* ── Round card ─────────────────────────────────── */}
          <motion.div
            className={`ph-round${feat.live ? ' is-live' : ''}`
              + (slotIsBoard ? '' : ' is-feature')}
            variants={fadeUp}>
            <div className="ph-photo ph-photo-round"
                 style={{ backgroundImage: `url(${courseAerialSrc})` }} aria-hidden="true" />
            <div className="ph-tint ph-tint-round" aria-hidden="true" />
            <div className="ph-scrim-round" aria-hidden="true" />
            {feat.live && <div className="ph-round-accent" aria-hidden="true" />}

            {feat.pending ? (
              <div className="ph-round-inner">
                <div className="ph-skel ph-skel-round" aria-hidden="true">
                  <span /><span /><span />
                </div>
              </div>
            ) : (
            <div className="ph-round-inner">
              <div className="ph-round-eyebrow-row">
                {feat.live && (
                  <span className="ph-pulse" aria-hidden="true">
                    <span className="ph-pulse-dot" />
                    <span className="ph-pulse-ring" />
                  </span>
                )}
                <span className={`ph-round-eyebrow${feat.live ? ' live' : ''}`}>{feat.eyebrow}</span>
              </div>

              <div className="ph-round-title">{feat.title}</div>

              {(feat.metaA || feat.metaB) && (
                <div className="ph-round-meta">
                  {feat.metaA && <span>{feat.metaA}</span>}
                  {feat.metaA && feat.metaB && <span className="ph-meta-sep" />}
                  {feat.metaB && <span className="ph-round-meta-where">{feat.metaB}</span>}
                </div>
              )}

              <div className="ph-round-foot">
                {feat.statValue && (
                  <div className="ph-round-stat">
                    <div className="ph-round-stat-val">{feat.statValue}</div>
                    <div className="ph-round-stat-lbl">{feat.statLabel}</div>
                  </div>
                )}
                <button className={`ph-round-cta ph-cta-${feat.ctaKind}`} onClick={feat.action}>
                  {feat.cta}
                  <ChevronRight size={17} />
                </button>
              </div>
            </div>
            )}
          </motion.div>

          {/* ── Elsewhere in the society ─────────────────────
              ABOVE the middle slot, which is history — the live board of a
              round that has finished, or the reader's own recent form. Who is
              out on the course RIGHT NOW is the one thing on this page with a
              clock on it, and it was below both. Hidden entirely when nobody
              else is out — never an empty row. */}
          <AnimatePresence initial={false}>
          {elsewhereShown.length > 0 && (
            <LateCard key="elsewhere" late={entered} className="ph-elsewhere">
              <div className="ph-elsewhere-head">
                <span className="ph-elsewhere-title">
                  <span className="ph-pulse-dot sm" aria-hidden="true" />
                  ELSEWHERE IN THE SOCIETY
                </span>
              </div>
              <div className="ph-elsewhere-grid">
                {elsewhereShown.map((p) => {
                  const tp = feedToPar(p);
                  return (
                    <button
                      key={p.player_id}
                      className="ph-elsewhere-cell"
                      onClick={() => navigate(`/leaderboard?tournament=${p.tournament_id}&live=true`)}
                      title={`${p.name} · ${p.tournament_name}`}
                    >
                      <span className="ph-el-avatar-wrap">
                        <span className="ph-el-avatar">{p.initials}</span>
                        <span className="ph-el-presence" aria-hidden="true" />
                      </span>
                      <span className="ph-el-text">
                        <span className="ph-el-name">{p.short_name}</span>
                        <span className="ph-el-score">
                          {tp && <span className={tp.cls}>{tp.txt}</span>}
                          <span className="ph-el-thru">{tp ? ' · ' : ''}{ordinalHole(p.thru)}</span>
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </LateCard>
          )}
          </AnimatePresence>


          {/* ── Middle slot: live board / matches / recent form ── */}
          <motion.div className={`ph-slot${slotIsBoard ? '' : ' is-compact'}`}
                      variants={fadeUp}>
            <motion.div key={slotKind} className="ph-slot-inner"
                        initial={entered ? swapIn : false} animate={swapShow}>
            {slotKind === 'pending' ? (
              <>
                <div className="ph-slot-head">
                  <span className="ph-slot-title">{ocTid ? 'LIVE LEADERBOARD' : 'RECENT FORM'}</span>
                </div>
                <div className="ph-skel ph-skel-slot" aria-hidden="true">
                  <span /><span /><span />
                </div>
              </>
            ) : slotKind === 'matchplay' ? (
              <>
                <div className="ph-slot-head">
                  <span className="ph-slot-title">MATCHPLAY</span>
                  {hasLiveScoring && <span className="ph-badge-live">LIVE</span>}
                </div>
                <div className="ph-slot-rows">
                  {mpMatches.slice(0, 3).map((m, i) => (
                    <MpRow key={m.match_number ?? i} match={m} />
                  ))}
                </div>
                <div className="ph-slot-foot">
                  <span>Updates every 60 seconds</span>
                  <button className="ph-slot-link"
                          onClick={() => navigate(`/leaderboard?tournament=${boardTid}&live=true`)}>
                    All matches ›
                  </button>
                </div>
              </>
            ) : slotKind === 'board' ? (
              <>
                <div className="ph-slot-head">
                  <span className="ph-slot-title">LIVE LEADERBOARD</span>
                  {hasLiveScoring && <span className="ph-badge-live">LIVE</span>}
                </div>
                <div className="ph-slot-rows">
                  {boardTop3.map((e, i) => (
                    <LbRow key={e.player_id ?? i} entry={e} idx={i}
                           isStrokePlay={isStrokePlay}
                           isMe={String(e.player_id) === String(playerId)} />
                  ))}
                </div>
                <div className="ph-slot-foot">
                  <span>Updates every 60 seconds</span>
                  <button className="ph-slot-link"
                          onClick={() => navigate(`/leaderboard?tournament=${boardTid}&live=true`)}>
                    Full board ›
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="ph-slot-head">
                  <span className="ph-slot-title">RECENT FORM</span>
                  <span className="ph-badge-quiet">GROSS</span>
                </div>
                {insights ? (
                  <>
                    {/* THE GROSS SCORE — the number a golfer says out loud,
                        and the one figure that means the same thing whether
                        the day was scored in points or in strokes. Lowest is
                        best. `recentForm` only puts an eighteen-hole
                        individual card on this scale. */}
                    <div className="ph-form-tiles">
                      {insights.tiles.map((t) => (
                        <div key={t.key}
                             className={`ph-form-tile${t.best ? ' best' : ''}`}>
                          {t.gross}
                        </div>
                      ))}
                    </div>
                    <div className="ph-form-line">
                      <span>Avg <b>{insights.avg}</b></span>
                      <span className="ph-meta-sep" />
                      <span>Best <b className="ph-sc-good">{insights.best}</b></span>
                      <span className="ph-meta-sep" />
                      {/* Level par IS playing to handicap, in both formats —
                          which is what the old "vs hcp" line meant to say.

                          "n OF m", because this is the only figure on the line
                          that is a COUNT. Beside "Avg +5.3" and "Best E" — both
                          to-par — a bare "2 to hcp" reads as two OVER handicap,
                          which is very nearly its opposite. The denominator is
                          what makes it unmistakably a tally of rounds. */}
                      <span className={insights.toHandicap > 0 ? 'ph-sc-good' : ''}>
                        {insights.toHandicap} of {insights.count} to hcp
                      </span>
                      {insights.stronger && (
                        <>
                          <span className="ph-meta-sep" />
                          <span>{insights.stronger} stronger</span>
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="ph-slot-empty">Your story starts here</div>
                )}
                <div className="ph-slot-foot">
                  <span>
                    {formFootnote(insights)}
                  </span>
                  <button className="ph-slot-link" onClick={() => navigate('/my-stats')}>
                    My stats ›
                  </button>
                </div>
              </>
            )}
            </motion.div>
          </motion.div>

          {/* ── Recent activity + Upcoming ───────────────────── */}
          <motion.div className="ph-duo" variants={fadeUp}>
            {/* Two rows, two players, each row its own target — so the card
                itself is not the button any more: a row leads to ITS
                tournament, and nesting a button inside a button is not
                markup a browser will honour. */}
            <div className="ph-duo-card ph-duo-card--static">
              <div className="ph-duo-label">RECENT ACTIVITY</div>
              {activityItems.length > 0 ? (
                <div className="ph-act-list">
                  {/* No to-par pill beside these any more. The line now carries
                      the figure the format was played for — points, a stroke
                      total, or the match — so the pill was the same fact twice,
                      and at 360px it was the 40px that ellipsised the name. */}
                  {activityItems.map((a) => (
                    <button
                      key={a.round_id}
                      className="ph-act-row"
                      onClick={() => navigate(`/leaderboard?tournament=${a.tournament_id}`)}
                    >
                      <span className="ph-act-avatar">{a.initials}</span>
                      <span className="ph-act-text">
                        <span className="ph-act-line">{activityLine(a)}</span>
                        <span className="ph-act-when">{relativeDay(a.date_played)}</span>
                      </span>
                    </button>
                  ))}
                </div>
              ) : feedKnown ? (
                <div className="ph-duo-empty">Nothing played this week</div>
              ) : (
                <div className="ph-duo-skeleton" aria-hidden="true">
                  <span /><span />
                </div>
              )}
            </div>

            <button
              className="ph-duo-card"
              onClick={() => {
                if (!nextUp) return navigate('/play');
                if (nextUp.is_current) {
                  navigate(`/leaderboard?tournament=${nextUp.tournament_id}&live=true`);
                } else {
                  navigate(`/tournaments/${nextUp.tournament_id}/teesheet`);
                }
              }}
            >
              <div className="ph-duo-label-row">
                <span className="ph-duo-label">{nextUp?.is_current ? 'IN PLAY' : 'UPCOMING'}</span>
                <ChevronRight size={13} className="ph-duo-chev" />
              </div>
              {readiness.statusPending ? (
                <div className="ph-duo-skeleton" aria-hidden="true">
                  <span /><span />
                </div>
              ) : nextUp ? (
                <>
                  <div className="ph-duo-date">
                    {nextUp.is_current ? 'Today' : fmtDate(nextUp.tournament_date)}
                  </div>
                  <div className="ph-duo-detail">{nextUp.tournament_name}</div>
                </>
              ) : (
                <>
                  <div className="ph-duo-date">No round booked</div>
                  <div className="ph-duo-detail">Find a tee time</div>
                </>
              )}
            </button>
          </motion.div>

        </motion.div>
      ) : (
        <div className="ph-welcome-wrap">
          <WelcomeCard />
        </div>
      )}

      <BottomNav />

      <ClaimPlayerSheet />

      <HandicapKeypadModal
        isOpen={showHcp}
        currentHandicap={handicap}
        onSave={saveHcp}
        onClose={() => setShowHcp(false)}
        saving={savingHcp}
      />
    </div>
  );
}
